import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { DrawToolId, MediaOverlays, OverlayFontId, OverlayStroke, OverlayText } from '../types';
import {
  MAX_POINTS, OVERLAY_COLORS, OVERLAY_FONTS, THICKNESS_PRESETS, TOOL_STYLE,
  countPoints, emptyOverlays, eraseAt, fontById, newId, round4, strokePath,
} from '../utils/overlays';
import XMarkIcon from './icons/XMarkIcon';

interface MediaEditorProps {
  mediaUrl: string;
  mediaType: 'image' | 'video';
  initial?: MediaOverlays | null;
  onDone: (overlays: MediaOverlays | null) => void;
  onCancel: () => void;
}

type Mode = 'text' | 'draw';
type Tool = DrawToolId | 'eraser';
interface Snapshot { texts: OverlayText[]; strokes: OverlayStroke[] }

const ToolIcon: React.FC<{ tool: Tool; className?: string }> = ({ tool, className = 'h-6 w-6' }) => {
  const common = { className, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (tool === 'pen') return <svg {...common}><path d="M4 20l1-4L16.5 4.5a2.1 2.1 0 0 1 3 3L8 19z" /><path d="M14.5 6.5l3 3" /></svg>;
  if (tool === 'brush') return <svg {...common}><path d="M18.5 3.5c-3 1.5-6.5 5-8 8l2.5 2.5c3-1.5 6.5-5 8-8z" /><path d="M10.5 11.5C8 11.5 6.5 13 6.5 15.5c0 1.5-.8 2.6-2.5 3.5 3 .8 6.5.5 8-1.5l-2-2" /></svg>;
  if (tool === 'highlighter') return <svg {...common}><path d="M14 4l6 6-8 8H6v-6z" /><path d="M4 21h8" /></svg>;
  return <svg {...common}><path d="M8.5 20h11" /><path d="m5 15 8-8 6 6-5 5H8.5z" /></svg>;
};

const TOOL_LABEL: Record<Tool, string> = { pen: 'Pen', brush: 'Brush', highlighter: 'Highlight', eraser: 'Eraser' };

const MediaEditor: React.FC<MediaEditorProps> = ({ mediaUrl, mediaType, initial, onDone, onCancel }) => {
  const [aspect, setAspect] = useState(initial?.aspect || 1);
  const [texts, setTexts] = useState<OverlayText[]>(initial?.texts ?? []);
  const [strokes, setStrokes] = useState<OverlayStroke[]>(initial?.strokes ?? []);
  const [past, setPast] = useState<Snapshot[]>([]);
  const [future, setFuture] = useState<Snapshot[]>([]);
  const [mode, setMode] = useState<Mode>('text');
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState('#ffffff');
  const [thickness, setThickness] = useState(THICKNESS_PRESETS[1]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [limitHit, setLimitHit] = useState(false);

  const boxRef = useRef<HTMLDivElement>(null);
  const current = useRef({ texts, strokes });
  current.current = { texts, strokes };
  const gesture = useRef<{ kind: 'draw' | 'erase' | 'move'; id?: string; offX?: number; offY?: number; last?: [number, number]; moved?: boolean } | null>(null);
  const pointTotal = useMemo(() => strokes.reduce((n, s) => n + s.points.length / 2, 0), [strokes]);

  const pushHistory = useCallback(() => {
    setPast((p) => [...p.slice(-40), { texts: current.current.texts, strokes: current.current.strokes }]);
    setFuture([]);
  }, []);

  const undo = () => {
    setPast((p) => {
      if (!p.length) return p;
      const prev = p[p.length - 1];
      setFuture((f) => [...f, { texts: current.current.texts, strokes: current.current.strokes }]);
      setTexts(prev.texts);
      setStrokes(prev.strokes);
      return p.slice(0, -1);
    });
  };
  const redo = () => {
    setFuture((f) => {
      if (!f.length) return f;
      const next = f[f.length - 1];
      setPast((p) => [...p, { texts: current.current.texts, strokes: current.current.strokes }]);
      setTexts(next.texts);
      setStrokes(next.strokes);
      return f.slice(0, -1);
    });
  };

  // ---- Coordinates: pointer -> 0..1 of the media frame
  const toLocal = (e: { clientX: number; clientY: number }): [number, number] => {
    const r = boxRef.current!.getBoundingClientRect();
    return [Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))];
  };

  // ---- Drawing
  const onDrawDown = (e: React.PointerEvent) => {
    if (mode !== 'draw') return;
    e.preventDefault();
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* synthetic/inactive pointer */ }
    const [x, y] = toLocal(e);
    pushHistory();
    setSelectedId(null);
    if (tool === 'eraser') {
      gesture.current = { kind: 'erase', last: [x, y] };
      setStrokes((s) => eraseAt(s, x, y, thickness * 3.2, aspect));
      return;
    }
    if (countPoints({ aspect, texts: [], strokes: current.current.strokes }) >= MAX_POINTS) { setLimitHit(true); gesture.current = null; return; }
    const id = newId();
    gesture.current = { kind: 'draw', id, last: [x, y] };
    setStrokes((s) => [...s, { id, tool, color, width: thickness, points: [round4(x), round4(y)] }]);
  };

  const onDrawMove = (e: React.PointerEvent) => {
    const g = gesture.current;
    if (!g || mode !== 'draw') return;
    const [x, y] = toLocal(e);
    const [lx, ly] = g.last || [x, y];
    if (Math.hypot(x - lx, (y - ly) / aspect) < 0.0035) return;
    g.last = [x, y];
    if (g.kind === 'erase') {
      // Sweep along the whole segment (fast swipes only report a few pointer events).
      const r = thickness * 3.2;
      const dist = Math.hypot(x - lx, (y - ly) / aspect);
      const steps = Math.max(1, Math.ceil(dist / (r * 0.6)));
      setStrokes((s) => {
        let next = s;
        for (let i = 1; i <= steps; i++) next = eraseAt(next, lx + ((x - lx) * i) / steps, ly + ((y - ly) * i) / steps, r, aspect);
        return next;
      });
      return;
    }
    if (pointTotal >= MAX_POINTS) { setLimitHit(true); return; }
    setStrokes((s) => s.map((st) => (st.id === g.id ? { ...st, points: [...st.points, round4(x), round4(y)] } : st)));
  };

  const onDrawUp = () => { gesture.current = null; };

  // ---- Text
  const addText = () => {
    pushHistory();
    const t: OverlayText = { id: newId(), text: '', font: 'classic', color: '#ffffff', x: 0.5, y: 0.5, size: 0.08, box: false };
    setTexts((arr) => [...arr, t]);
    setSelectedId(t.id);
    setMode('text');
  };
  const selected = texts.find((t) => t.id === selectedId) || null;
  const patchSelected = (patch: Partial<OverlayText>) => setTexts((arr) => arr.map((t) => (t.id === selectedId ? { ...t, ...patch } : t)));
  const removeSelected = () => {
    if (!selectedId) return;
    pushHistory();
    setTexts((arr) => arr.filter((t) => t.id !== selectedId));
    setSelectedId(null);
  };

  const onTextDown = (e: React.PointerEvent, t: OverlayText) => {
    if (mode !== 'text') return;
    e.stopPropagation();
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* synthetic/inactive pointer */ }
    const [x, y] = toLocal(e);
    setSelectedId(t.id);
    gesture.current = { kind: 'move', id: t.id, offX: t.x - x, offY: t.y - y, moved: false };
  };
  const onTextMove = (e: React.PointerEvent) => {
    const g = gesture.current;
    if (!g || g.kind !== 'move') return;
    if (!g.moved) { g.moved = true; pushHistory(); }
    const [x, y] = toLocal(e);
    const nx = Math.min(1, Math.max(0, x + (g.offX || 0)));
    const ny = Math.min(1, Math.max(0, y + (g.offY || 0)));
    setTexts((arr) => arr.map((t) => (t.id === g.id ? { ...t, x: nx, y: ny } : t)));
  };

  const finish = () => {
    const cleanTexts = texts.filter((t) => t.text.trim());
    if (!cleanTexts.length && !strokes.length) { onDone(null); return; }
    onDone({ ...emptyOverlays(aspect), texts: cleanTexts, strokes });
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const h = 1000 / aspect;
  const boxWidth = `min(100cqw, calc(100cqh * ${aspect}))`;
  const chipBase = 'shrink-0 rounded-full px-3.5 py-2 text-sm font-bold transition';

  return createPortal(
    <div className="fixed inset-0 z-[7000] flex flex-col bg-black text-white" role="dialog" aria-label="Edit media">
      {/* Top bar */}
      <div className="flex shrink-0 items-center justify-between px-3 pb-2" style={{ paddingTop: 'calc(var(--sat-native, env(safe-area-inset-top)) + 0.625rem)' }}>
        <button onClick={onCancel} className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/25 transition active:scale-90" aria-label="Cancel editing">
          <XMarkIcon className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-1">
          <button onClick={undo} disabled={!past.length} aria-label="Undo" className="flex h-11 w-11 items-center justify-center rounded-xl transition active:scale-90 disabled:opacity-30">
            <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M9 14 4 9l5-5" /><path d="M4 9h10a6 6 0 0 1 0 12h-3" /></svg>
          </button>
          <button onClick={redo} disabled={!future.length} aria-label="Redo" className="flex h-11 w-11 items-center justify-center rounded-xl transition active:scale-90 disabled:opacity-30">
            <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="m15 14 5-5-5-5" /><path d="M20 9H10a6 6 0 0 0 0 12h3" /></svg>
          </button>
        </div>
        <button onClick={finish} className="rounded-xl bg-white px-5 py-2.5 text-sm font-black text-black transition active:scale-95">Done</button>
      </div>

      {/* Stage */}
      <div className="relative min-h-0 flex-1" style={{ containerType: 'size' }} onPointerDown={() => { if (mode === 'text') setSelectedId(null); }}>
        <div ref={boxRef} className="absolute left-1/2 top-1/2 select-none overflow-hidden" style={{ width: boxWidth, aspectRatio: String(aspect), transform: 'translate(-50%, -50%)', containerType: 'inline-size' }}>
          {mediaType === 'video' ? (
            <video src={mediaUrl} className="absolute inset-0 h-full w-full object-cover" autoPlay muted loop playsInline onLoadedMetadata={(e) => { const v = e.currentTarget; if (v.videoWidth && v.videoHeight) setAspect(v.videoWidth / v.videoHeight); }} />
          ) : (
            <img src={mediaUrl} alt="" draggable={false} className="absolute inset-0 h-full w-full object-cover" onLoad={(e) => { const i = e.currentTarget; if (i.naturalWidth && i.naturalHeight) setAspect(i.naturalWidth / i.naturalHeight); }} />
          )}

          <svg viewBox={`0 0 1000 ${h}`} className="pointer-events-none absolute inset-0 h-full w-full" preserveAspectRatio="none">
            {strokes.map((s) => {
              const st = TOOL_STYLE[s.tool] || TOOL_STYLE.pen;
              return <path key={s.id} d={strokePath(s.points, h)} fill="none" stroke={s.color} strokeWidth={s.width * 1000 * st.mult} strokeOpacity={st.opacity} strokeLinecap={st.cap} strokeLinejoin="round" />;
            })}
          </svg>

          {texts.map((t) => {
            const f = fontById(t.font);
            const isSel = t.id === selectedId;
            return (
              <div
                key={t.id}
                onPointerDown={(e) => onTextDown(e, t)}
                onPointerMove={onTextMove}
                onPointerUp={onDrawUp}
                onPointerCancel={onDrawUp}
                className={`absolute whitespace-pre-wrap text-center leading-tight ${mode === 'text' ? 'cursor-move' : 'pointer-events-none'}`}
                style={{
                  left: `${t.x * 100}%`, top: `${t.y * 100}%`, transform: 'translate(-50%, -50%)', maxWidth: '90%',
                  fontFamily: f.family, fontWeight: f.weight, fontSize: `${t.size * 100}cqw`, color: t.color,
                  textShadow: t.box ? 'none' : '0 1px 6px rgba(0,0,0,0.45)',
                  background: t.box ? 'rgba(0,0,0,0.55)' : 'transparent',
                  padding: t.box ? '0.18em 0.4em' : '0.1em 0.2em',
                  borderRadius: t.box ? '0.3em' : '0.2em',
                  outline: isSel && mode === 'text' ? '1.5px dashed rgba(255,255,255,0.85)' : 'none',
                  touchAction: 'none',
                  minWidth: '1.5em',
                }}
              >
                {t.text || <span style={{ opacity: 0.45 }}>Type…</span>}
              </div>
            );
          })}

          {mode === 'draw' && (
            <div
              className="absolute inset-0 z-[5] cursor-crosshair"
              style={{ touchAction: 'none' }}
              onPointerDown={onDrawDown}
              onPointerMove={onDrawMove}
              onPointerUp={onDrawUp}
              onPointerCancel={onDrawUp}
            />
          )}
        </div>
        {limitHit && <p className="pointer-events-none absolute inset-x-0 top-3 mx-auto w-fit rounded-full bg-black/75 px-4 py-2 text-xs font-bold">Drawing limit reached</p>}
      </div>

      {/* Tools */}
      <div className="shrink-0 border-t border-white/10 bg-black px-3 pt-3" style={{ paddingBottom: 'calc(var(--sab-native, env(safe-area-inset-bottom)) + 0.75rem)' }}>
        <div className="mb-3 grid grid-cols-2 rounded-2xl border border-white/15 p-1" role="tablist" aria-label="Edit mode">
          <button role="tab" aria-selected={mode === 'text'} onClick={() => setMode('text')} className={`rounded-xl py-2.5 text-base font-black transition ${mode === 'text' ? 'bg-white text-black' : 'text-white/60'}`}>Aa  Text</button>
          <button role="tab" aria-selected={mode === 'draw'} onClick={() => { setMode('draw'); setSelectedId(null); }} className={`rounded-xl py-2.5 text-base font-black transition ${mode === 'draw' ? 'bg-white text-black' : 'text-white/60'}`}>Draw</button>
        </div>

        {mode === 'text' ? (
          selected ? (
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  autoFocus
                  value={selected.text}
                  onChange={(e) => patchSelected({ text: e.target.value.slice(0, 80) })}
                  placeholder="Type your text"
                  aria-label="Text"
                  className="min-w-0 flex-1 rounded-xl border border-white/20 bg-white/[0.06] px-4 py-3 text-base text-white placeholder:text-white/40 focus:border-white focus:outline-none"
                />
                <button onClick={removeSelected} aria-label="Delete text" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-white/20 transition active:scale-90">
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16" /><path d="M9 7V4.5h6V7" /><path d="M6.5 7l.8 12.5h9.4L17.5 7" /></svg>
                </button>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="radiogroup" aria-label="Font">
                {OVERLAY_FONTS.map((f) => (
                  <button key={f.id} role="radio" aria-checked={selected.font === f.id} onClick={() => patchSelected({ font: f.id as OverlayFontId })}
                    className={`${chipBase} text-lg ${selected.font === f.id ? 'bg-white text-black' : 'bg-white/10 text-white'}`} style={{ fontFamily: f.family, fontWeight: f.weight }}>
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="radiogroup" aria-label="Text color">
                {OVERLAY_COLORS.map((c) => (
                  <button key={c} role="radio" aria-checked={selected.color === c} aria-label={c} onClick={() => patchSelected({ color: c })}
                    className={`h-8 w-8 shrink-0 rounded-full border-2 ${selected.color === c ? 'border-white scale-110' : 'border-white/30'}`} style={{ backgroundColor: c }} />
                ))}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-white/50">Size</span>
                <input type="range" min={0.03} max={0.2} step={0.005} value={selected.size} onChange={(e) => patchSelected({ size: Number(e.target.value) })} className="min-w-0 flex-1 accent-white" aria-label="Text size" />
                <button onClick={() => patchSelected({ box: !selected.box })} aria-pressed={!!selected.box} className={`${chipBase} ${selected.box ? 'bg-white text-black' : 'bg-white/10 text-white'}`}>Box</button>
              </div>
            </div>
          ) : (
            <button onClick={addText} className="w-full rounded-2xl bg-white py-3.5 text-base font-black text-black transition active:scale-[0.98]">+ Add text</button>
          )
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2" role="radiogroup" aria-label="Drawing tool">
              {(['pen', 'brush', 'highlighter', 'eraser'] as Tool[]).map((t) => (
                <button key={t} role="radio" aria-checked={tool === t} onClick={() => setTool(t)}
                  className={`flex flex-col items-center gap-1 rounded-2xl border py-2.5 text-[11px] font-bold transition active:scale-95 ${tool === t ? 'border-white bg-white text-black' : 'border-white/20 text-white'}`}>
                  <ToolIcon tool={t} />
                  {TOOL_LABEL[t]}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex shrink-0 items-center gap-2" role="radiogroup" aria-label="Thickness">
                {THICKNESS_PRESETS.map((t, i) => (
                  <button key={t} role="radio" aria-checked={thickness === t} aria-label={`Thickness ${i + 1}`} onClick={() => setThickness(t)}
                    className={`flex h-11 w-11 items-center justify-center rounded-xl border transition active:scale-90 ${thickness === t ? 'border-white bg-white/15' : 'border-white/20'}`}>
                    <span className="rounded-full bg-white" style={{ width: 6 + i * 6, height: 6 + i * 6 }} />
                  </button>
                ))}
              </div>
              <input type="range" min={0.002} max={0.03} step={0.001} value={thickness} onChange={(e) => setThickness(Number(e.target.value))} className="min-w-0 flex-1 accent-white" aria-label="Custom thickness" />
            </div>
            {tool !== 'eraser' && (
              <div className="flex items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="radiogroup" aria-label="Color">
                {OVERLAY_COLORS.map((c) => (
                  <button key={c} role="radio" aria-checked={color === c} aria-label={c} onClick={() => setColor(c)}
                    className={`h-8 w-8 shrink-0 rounded-full border-2 ${color === c ? 'border-white scale-110' : 'border-white/30'}`} style={{ backgroundColor: c }} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default MediaEditor;
